import React, { useState } from "react";
import logo from "../assets/logo.jpg";
import { sendPasswordResetEmail, confirmPasswordReset, verifyPasswordResetCode } from "firebase/auth";
import { auth } from "../firebase";

export default function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // Check for password reset mode in URL
  const urlParams = new URLSearchParams(window.location.search);
  const mode = urlParams.get("mode");
  const oobCode = urlParams.get("oobCode");
  const isResetMode = mode === "resetPassword" && oobCode;

  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetLoading, setResetLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const handlePasswordReset = async () => {
    if(!newPassword){ setResetError("Please enter a password."); return; }
    if(newPassword.length < 6){ setResetError("Password must be at least 6 characters."); return; }
    if(newPassword !== confirmPassword){ setResetError("Passwords don't match."); return; }
    setResetLoading(true);
    setResetError("");
    try{
      await verifyPasswordResetCode(auth, oobCode);
      await confirmPasswordReset(auth, oobCode, newPassword);
      setResetSuccess(true);
      // Clean up URL
      window.history.replaceState({}, document.title, "/");
    }catch(e){
      if(e.code==="auth/invalid-action-code"){
        setResetError("This link has expired or already been used. Please request a new invite.");
      } else if(e.code==="auth/weak-password"){
        setResetError("Password is too weak. Please use at least 6 characters.");
      } else {
        setResetError("Failed to set password: "+e.message);
      }
    }
    setResetLoading(false);
  };

  const handleForgot = async () => {
    if(!forgotEmail.trim()){ alert("Please enter your email address."); return; }
    setForgotLoading(true);
    try{
      await sendPasswordResetEmail(auth, forgotEmail.trim(), {
        url: "https://portal.ficsoriaccounting.com",
      });
      setForgotSent(true);
    }catch(e){
      if(e.code==="auth/user-not-found"){
        alert("No account found for that email. Please contact Ficsori Accounting to get access.");
      } else {
        alert("Failed to send reset email: "+e.message);
      }
    }
    setForgotLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await onLogin(email, password);
    } catch (err) {
      setError("Invalid email or password. Please try again.");
    }
    setLoading(false);
  };

  // Password Reset Mode
  if(isResetMode){
    return (
      <div style={{minHeight:"100vh",background:"#f5f5f0",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif"}}>
        <div style={{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:16,padding:"36px 44px",width:400,boxShadow:"0 2px 24px rgba(0,0,0,0.06)"}}>
          <div style={{textAlign:"center",marginBottom:20}}>
            <img src={logo} alt="Ficsori Accounting" style={{height:68,objectFit:"contain"}}/>
          </div>
          {resetSuccess ? (
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:40,marginBottom:12}}>✅</div>
              <div style={{fontSize:15,fontWeight:500,color:"#3B6D11",marginBottom:8}}>Password set successfully!</div>
              <div style={{fontSize:13,color:"#555",marginBottom:20,lineHeight:1.6}}>
                Your password has been created. You can now log in to your Ficsori Accounting Client Portal.
              </div>
              <button
                onClick={()=>window.location.href="/"}
                style={{width:"100%",padding:"10px",background:"#C0111F",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"sans-serif"}}>
                Log In to Your Portal →
              </button>
            </div>
          ) : (
            <>
              <div style={{textAlign:"center",marginBottom:20}}>
                <div style={{fontSize:15,fontWeight:500,marginBottom:4}}>Set Up Your Password</div>
                <div style={{fontSize:12,color:"#888"}}>Create a password to access your Ficsori Accounting Client Portal</div>
              </div>
              <div style={{marginBottom:12}}>
                <div style={{fontSize:11,color:"#666",marginBottom:4}}>New Password</div>
                <div style={{position:"relative"}}>
                  <input type={showNewPassword?"text":"password"} value={newPassword} onChange={e=>setNewPassword(e.target.value)}
                    placeholder="At least 6 characters"
                    style={{width:"100%",padding:"8px 36px 8px 10px",fontSize:13,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",boxSizing:"border-box"}}
                    onKeyDown={e=>e.key==="Enter"&&handlePasswordReset()}/>
                  <div onClick={()=>setShowNewPassword(s=>!s)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",cursor:"pointer",fontSize:16,color:"#aaa"}}>
                    {showNewPassword?"🙈":"👁"}
                  </div>
                </div>
              </div>
              <div style={{marginBottom:16}}>
                <div style={{fontSize:11,color:"#666",marginBottom:4}}>Confirm Password</div>
                <div style={{position:"relative"}}>
                  <input type={showNewPassword?"text":"password"} value={confirmPassword} onChange={e=>setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    style={{width:"100%",padding:"8px 36px 8px 10px",fontSize:13,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",boxSizing:"border-box"}}
                    onKeyDown={e=>e.key==="Enter"&&handlePasswordReset()}/>
                  <div onClick={()=>setShowNewPassword(s=>!s)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",cursor:"pointer",fontSize:16,color:"#aaa"}}>
                    {showNewPassword?"🙈":"👁"}
                  </div>
                </div>
              </div>
              {resetError&&<div style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"8px 10px",borderRadius:8,marginBottom:12}}>{resetError}</div>}
              <button onClick={handlePasswordReset} disabled={resetLoading}
                style={{width:"100%",padding:"10px",background:"#C0111F",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"sans-serif"}}>
                {resetLoading?"Setting up...":"Set My Password & Access Portal"}
              </button>
              <div style={{textAlign:"center",marginTop:12,fontSize:11,color:"#aaa"}}>
                Already have a password? <span style={{color:"#C0111F",cursor:"pointer"}} onClick={()=>window.location.href="/"}>Log in here</span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight:"100vh",background:"#f5f5f0",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"sans-serif"}}>
      <div style={{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:16,padding:"36px 44px",width:400,boxShadow:"0 2px 24px rgba(0,0,0,0.06)"}}>
        <div style={{textAlign:"center",marginBottom:20}}>
          <img src={logo} alt="Ficsori Accounting" style={{height:68,objectFit:"contain"}}/>
        </div>
        <div style={{textAlign:"center",marginBottom:24}}>
          <div style={{fontSize:11,fontStyle:"italic",color:"#888",marginBottom:4}}>"Knowing your numbers is knowing your power."</div>
          <div style={{fontSize:12,color:"#aaa"}}>Client Portal — Sign In</div>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{marginBottom:14}}>
            <div style={{fontSize:11,color:"#666",marginBottom:4}}>Email</div>
            <input type="email" value={email} onChange={e=>setEmail(e.target.value)} required placeholder="your@email.com"
              style={{width:"100%",padding:"8px 10px",fontSize:13,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",boxSizing:"border-box"}}/>
          </div>
          <div style={{marginBottom:20}}>
            <div style={{fontSize:11,color:"#666",marginBottom:4}}>Password</div>
            <div style={{position:"relative"}}>
              <input type={showPassword?"text":"password"} value={password} onChange={e=>setPassword(e.target.value)} required placeholder="••••••••"
                style={{width:"100%",padding:"8px 36px 8px 10px",fontSize:13,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",boxSizing:"border-box"}}/>
              <div onClick={()=>setShowPassword(s=>!s)} style={{position:"absolute",right:10,top:"50%",transform:"translateY(-50%)",cursor:"pointer",fontSize:16,color:"#aaa",userSelect:"none"}}>
                {showPassword ? "🙈" : "👁"}
              </div>
            </div>
          </div>
          {error && <div style={{fontSize:12,color:"#A32D2D",background:"#FCEBEB",padding:"8px 10px",borderRadius:8,marginBottom:14}}>{error}</div>}
          <button type="submit" disabled={loading}
            style={{width:"100%",padding:"10px",background:"#C0111F",color:"#fff",border:"none",borderRadius:8,fontSize:13,fontWeight:500,cursor:"pointer",fontFamily:"sans-serif"}}>
            {loading?"Signing in...":"Sign In"}
          </button>
        {!showForgot ? (
          <div style={{textAlign:"center",marginTop:12}}>
            <span onClick={()=>{setShowForgot(true);setForgotEmail(email);}} style={{fontSize:12,color:"#C0111F",cursor:"pointer",textDecoration:"underline"}}>
              Forgot your password?
            </span>
          </div>
        ) : (
          <div style={{marginTop:16,padding:"14px 16px",background:"#f5f5f0",borderRadius:10}}>
            {forgotSent ? (
              <div style={{textAlign:"center"}}>
                <div style={{fontSize:13,fontWeight:500,color:"#3B6D11",marginBottom:6}}>✓ Password reset email sent!</div>
                <div style={{fontSize:12,color:"#555",marginBottom:10}}>Check your inbox for a link to set your new password. Then come back here to log in.</div>
                <button style={{fontSize:12,color:"#C0111F",background:"none",border:"none",cursor:"pointer",textDecoration:"underline"}} onClick={()=>{setShowForgot(false);setForgotSent(false);}}>Back to login</button>
              </div>
            ) : (
              <>
                <div style={{fontSize:12,fontWeight:500,marginBottom:8}}>Reset your password</div>
                <div style={{fontSize:11,color:"#888",marginBottom:8}}>Enter your email and we'll send you a link to set a new password.</div>
                <input
                  type="email" value={forgotEmail} onChange={e=>setForgotEmail(e.target.value)}
                  placeholder="your@email.com"
                  style={{width:"100%",padding:"7px 10px",fontSize:12,border:"0.5px solid #ccc",borderRadius:8,fontFamily:"sans-serif",boxSizing:"border-box",marginBottom:8}}
                  onKeyDown={e=>e.key==="Enter"&&handleForgot()}
                />
                <div style={{display:"flex",gap:8}}>
                  <button onClick={handleForgot} disabled={forgotLoading}
                    style={{flex:1,padding:"7px",background:"#C0111F",color:"#fff",border:"none",borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:"sans-serif"}}>
                    {forgotLoading?"Sending...":"Send Reset Link"}
                  </button>
                  <button onClick={()=>setShowForgot(false)}
                    style={{padding:"7px 12px",background:"#fff",border:"0.5px solid #ccc",borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:"sans-serif"}}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        )}
        </form>
        <div style={{marginTop:24,paddingTop:16,borderTop:"0.5px solid #eee"}}>
          <div style={{display:"flex",justifyContent:"center",gap:6,flexWrap:"wrap",marginBottom:10}}>
            <span style={{fontSize:9,padding:"2px 7px",borderRadius:4,background:"#E6F1FB",color:"#0C447C",border:"0.5px solid #85B7EB"}}>QB ProAdvisor L1</span>
            <span style={{fontSize:9,padding:"2px 7px",borderRadius:4,background:"#E1F5EE",color:"#085041",border:"0.5px solid #5DCAA5"}}>QB ProAdvisor L2</span>
            <span style={{fontSize:9,padding:"2px 7px",borderRadius:4,background:"#FAEEDA",color:"#633806",border:"0.5px solid #EF9F27"}}>QB Payroll Cert.</span>
          </div>
          <div style={{fontSize:10,color:"#bbb",textAlign:"center"}}>frank@ficsoriaccounting.com · (941) 780-4662</div>
        </div>
      </div>
    </div>
  );
}
